import * as Dialog from "@radix-ui/react-dialog";
import * as Form from "@radix-ui/react-form";
import { useEventListener } from "ahooks";
import Primary from "components/Buttons/Primary";
import { ModalContext } from "components/Providers/Modal";
import gsap from "gsap";
import icon from "images/global/icon.svg";
import AutoAnimate from "library/AutoAnimate";
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import colors from "styles/colors";
import textStyles from "styles/text";
import { z } from "zod";
import { free_email_providers } from "data/email-providers";
import { ReactComponent as ChevronDownIconSVG } from "images/global/chevron.svg";

import { Control } from "./Control";
import CurrentSoftware from "./CurrentSoftware";
import LinkButton from "components/Buttons/LinkButton";
import links from "utils/links";
import { SelectControl } from "./Select";

const portalId = "39878266";
const formId = "81f26411-aa43-4e83-9287-43a7580eb3ed";
const endpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

type SubmissionType = {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  company: string;
  numemployees: string;
  accounting_software:
    | ""
    | "QuickBooks"
    | "NetSuite"
    | "Sage Intacct"
    | "Xero"
    | "SAP"
    | "Workday"
    | "Dynamics 365"
    | "Other"
    | "None";
  free_form_text: string;
};

export default function ModalContent({
  type = "contact",
}: {
  type?: "contact" | "demo";
}) {
  const { setType: setContextType } = useContext(ModalContext);
  const [topShadow, setTopShadow] = useState(false);
  const [bottomShadow, setBottomShadow] = useState(false);
  const [formState, setFormState] = useState<"idle" | "submitting" | "error">(
    "idle"
  );
  const [isFormVisible, setIsFormVisible] = useState(true);
  const scrollable = useRef<HTMLDivElement>(null);
  const form = useRef<HTMLFormElement>(null);

  const onScroll = () => {
    if (!scrollable.current) return;
    const isScrollable =
      scrollable.current.scrollHeight > scrollable.current.clientHeight;
    if (!isScrollable) {
      setTopShadow(false);
      setBottomShadow(false);
      return;
    }

    setTopShadow(scrollable.current.scrollTop > 0);
    setBottomShadow(
      scrollable.current.scrollTop <
        scrollable.current.scrollHeight - scrollable.current.clientHeight
    );
  };

  useEffect(onScroll, []);
  useEventListener("resize", onScroll);

  const submit = async (data: FormData) => {
    console.log("Form submission started", { type });

    const baseSchema = z.object({
      firstname: z.string(),
      lastname: z.string(),
      email: z.string(),
      company: z.string(),
      phone: z.string(),
      numemployees: z.string(),
      message: z.string().optional(),
    });

    if (type === "demo") {
      const demoSchema = baseSchema.extend({
        accounting_software: z.enum([
          "QuickBooks",
          "NetSuite",
          "Sage Intacct",
          "Xero",
          "SAP",
          "Workday",
          "Dynamics 365",
          "Other",
          "None",
        ]),
      });

      const values = demoSchema.parse(Object.fromEntries(data));
      console.log("Demo form validated", values);

      const submission: SubmissionType = {
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        phone: values.phone,
        company: values.company,
        numemployees: values.numemployees,
        free_form_text: values.message ?? "",
        accounting_software: values.accounting_software,
      };

      console.log("Submitting to HubSpot:", submission);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fields: Object.entries(submission).map(([name, value]) => ({
            name,
            value,
          })),
        }),
      });

      console.log("HubSpot response:", {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("HubSpot API Error:", response.status, errorText);
        throw new Error(`Submission failed: ${response.status}`);
      }

      console.log("Demo form submitted successfully");

      const email_arr = submission?.email?.split("@");
      let email_provider: string | undefined = "";
      if (email_arr instanceof Array && email_arr.length === 2)
        email_provider = email_arr[1];
      let is_free_email_provider = false;
      free_email_providers.forEach((free_email_provider: string) => {
        if (free_email_provider === email_provider) {
          is_free_email_provider = true;
          return true;
        }
      });

      gsap.to(form.current, {
        yPercent: -150,
        ease: "power2.in",
      });
    } else {
      const contactSchema = baseSchema.extend({
        accounting_software: z.string().optional(),
      });

      const values = contactSchema.parse(Object.fromEntries(data));
      console.log("Contact form validated", values);

      const submission: SubmissionType = {
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        phone: values.phone,
        company: values.company,
        numemployees: values.numemployees,
        free_form_text: values.message ?? "",
        accounting_software: "",
      };

      console.log("Submitting to HubSpot:", submission);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fields: Object.entries(submission).map(([name, value]) => ({
            name,
            value,
          })),
        }),
      });

      console.log("HubSpot response:", {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("HubSpot API Error:", response.status, errorText);
        throw new Error(`Submission failed: ${response.status}`);
      }

      console.log("Contact form submitted successfully");
    }
  };

  return (
    <>
      <TopShadow $hasShadow={topShadow} />
      <Panel
        ref={scrollable}
        onScroll={onScroll}
        style={isFormVisible ? {} : { padding: "50px 100px 50px 0px" }}
      >
        <DialogText
          style={
            isFormVisible
              ? {}
              : { gap: 0, margin: 0, display: "flex", paddingLeft: "50px" }
          }
        >
          <CampfireIcon />
          <Title>
            <AutoAnimate duration={0.5}>
              {formState === "error" ? (
                <div key={formState}>
                  Something <span>broke</span>
                </div>
              ) : type === "contact" ? (
                <div key={type}>
                  Contact <span>Us</span>
                </div>
              ) : (
                <div key={type}>
                  Schedule a <span>Demo.</span>
                </div>
              )}
            </AutoAnimate>
          </Title>
          <Description>
            <AutoAnimate duration={0.5}>
              {formState === "error" ? (
                <Fragment key={formState}>
                  Something went wrong while sending your information. Check
                  your connection and try again. If the problem persists, drop
                  us a note at hello@meetcampfire.com.
                </Fragment>
              ) : type === "contact" ? (
                <Fragment key={type}>
                  Please provide your information, and will get back to you as
                  soon as possible! Thank you!
                </Fragment>
              ) : (
                <Fragment key={type}>
                  Let's get some basic info, and then we will get you on the
                  calendar.
                </Fragment>
              )}
            </AutoAnimate>

            <Row>
              <Invite>Interested in partnering?</Invite>
              <LinkButton
                className="contact-link"
                variant="arrow"
                greenOverride={true}
                to={links.partnerships}
              >
                Submit a request
              </LinkButton>
            </Row>
          </Description>
        </DialogText>
        {isFormVisible && (
          <FormWrapper
            data-portal-id={portalId}
            data-form-id={formId}
            ref={form}
            onSubmit={(e) => {
              e.preventDefault();
              console.log("Form onSubmit triggered");

              setFormState("submitting");
              submit(new FormData(e.currentTarget))
                .then(() => {
                  console.log("Form submission completed successfully");
                  setContextType(null);
                })
                .catch((error) => {
                  console.error("Form submission failed:", error);
                  setFormState("error");
                });
            }}
          >
            <SectionTitle>Details</SectionTitle>
            <TextInputs>
              <Control required name="firstname" placeholder="First Name" />
              <Control required name="lastname" placeholder="Last Name" />
              <Control
                required
                type="email"
                name="email"
                placeholder="Work Email"
              />
              <Control required name="company" placeholder="Company Name" />
              <Control type="phone" name="phone" placeholder="Phone #" />

              <SelectControl required name="numemployees" />

              {type === "contact" && (
                <Control
                  required
                  name="message"
                  asTextArea
                  placeholder="Message"
                />
              )}
            </TextInputs>
            {type === "demo" && (
              <>
                <SectionTitle>Current Accounting Software</SectionTitle>
                <CurrentSoftware />
              </>
            )}
            <SubmitArea $hasShadow={bottomShadow}>
              <FormSubmit type="submit">Submit</FormSubmit>
            </SubmitArea>
          </FormWrapper>
        )}
      </Panel>
    </>
  );
}

export const TopShadow = styled.div<{
  $hasShadow: boolean;
}>`
  display: none;
  ${({ $hasShadow }) =>
    fresponsive(css`
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 76px;
      background: ${colors.greenGray01};
      z-index: 2;
      transition: box-shadow 0.3s;

      ${$hasShadow &&
      css`
        box-shadow: 0 11px 32.7px 0 rgb(0 0 0 / 25%);
      `}
    `)}

  ${ftablet(css`
    display: block;
    height: 116px;
  `)}

  ${fmobile(css`
    display: block;
  `)}
`;
const Panel = styled.div`
  ${fresponsive(css`
    background: ${colors.greenGray01};
    display: flex;
    gap: 115px;
    overflow-y: auto;
    max-height: calc(100dvh - 16px);
  `)}

  ${ftablet(css`
    flex-direction: column;
    gap: 46px;
    overflow-y: auto;
    height: calc(100dvh - 32px);
    width: calc(100vw - 32px);
  `)}

  ${fmobile(css`
    width: calc(100vw - 16px);
    height: calc(100dvh - 16px);
    overflow-y: auto;
    padding: 76px 20px 0;
    flex-direction: column;
    gap: 46px;
  `)}
`;

export const DialogText = styled.div`
  ${fresponsive(css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: start;
    gap: 32px;
    margin-left: 60px;
    position: relative;
    z-index: 1;
  `)}

  ${ftablet(css`
    margin: 150px 142px 0;
  `)}

  ${fmobile(css`
    padding-left: 0 !important;
    margin: 0;
  `)}
`;

const CampfireIcon = styled.img.attrs({ src: icon, alt: "campfire logo" })`
  ${fresponsive(css`
    height: 50px;
  `)}

  ${fmobile(css`
    display: none;
  `)}
`;

const Title = styled(Dialog.Title)`
  ${textStyles.h2};
  color: ${colors.white};

  span {
    display: inline-block;
    color: ${colors.greenSaturated02};
  }

  ${ftablet(css`
    ${textStyles.h3};
  `)}

  ${fmobile(css`
    ${textStyles.h5};
  `)}
`;

const Description = styled(Dialog.Description).attrs({ as: "div" })`
  ${textStyles.bodyXL};
  color: ${colors.greenPastel01};

  ${fresponsive(css`
    width: 362px;
    min-height: 84px;
  `)}

  ${fmobile(css`
    width: 280px;
    ${textStyles.bodyL};
  `)}
`;

const FormWrapper = styled(Form.Root)`
  ${fresponsive(css`
    margin: 90px 148px 48px 0;
  `)}

  ${ftablet(css`
    margin: 0 142px 77px;
    width: 660px;
  `)}

  ${fmobile(css`
    margin: 0;
    width: 100%;
  `)}
`;

const SectionTitle = styled.div`
  ${textStyles.bodyS};
  color: ${colors.greenPastel01};

  ${fresponsive(css`
    margin-bottom: 20px;
  `)}

  ${fmobile(css`
    margin-bottom: 24px;
  `)}
`;

const TextInputs = styled.div`
  ${fresponsive(css`
    display: grid;
    gap: 24px;
    width: 529px;
    margin-bottom: 68px;

    & > * {
      grid-column: 1 / 3;
    }

    & > *:nth-child(3) {
      grid-column: 1 / 2;
    }

    & > *:nth-child(4) {
      grid-column: 2 / 3;
    }
  `)}

  ${ftablet(css`
    width: 660px;
    margin-bottom: 55px;
  `)}

  ${fmobile(css`
    width: 100%;
    gap: 15px;
    margin-bottom: 50px;

    & > * {
      grid-column: 1 / 2 !important;
    }
  `)}
`;

const SubmitArea = styled.div<{
  $hasShadow: boolean;
}>`
  ${fresponsive(css`
    margin-top: 68px;
  `)}

  ${({ $hasShadow }) =>
    fmobile(css`
      padding: 17px 14px;
      background: ${colors.greenGray01};
      position: sticky;
      bottom: 0;
      translate: -20px 0;
      width: calc(100vw - 16px);
      transition: box-shadow 0.3s;

      ${$hasShadow &&
      css`
        box-shadow: 0 -11px 32.7px 0 rgb(0 0 0 / 25%);
      `}
    `)}
`;

const FormSubmit = styled(Primary)`
  width: calc(100% + 18px);
  margin: 0 -9px;

  ${fmobile(css`
    width: 100%;
    margin: 0;
  `)}
`;

const Invite = styled.div`
  ${fresponsive(css`
    ${textStyles.bodyS};
    color: ${colors.greenPastel01};
  `)};

  ${ftablet(css`
    ${textStyles.bodyL};
  `)}

  ${fmobile(css`
    ${textStyles.bodyL};
    width: 100%;
    margin: 0;
  `)}
`;

const Row = styled.div`
  ${fresponsive(css`
    display: flex;
    margin-top: 20px;
    gap: 8px;
  `)}

  ${ftablet(css`
    white-space: nowrap;
  `)}

  ${fmobile(css`
    flex-direction: column;
  `)}
`;

const ChevronDownIcon = styled(ChevronDownIconSVG)`
  ${fresponsive(css`
    width: 16px;
    height: auto;
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);

    pointer-events: none;

    path {
      fill: ${colors.greenDark01};
    }
  `)}

  ${fmobile(css`
    width: 12px;
    height: 12px;
  `)}
`;

const Select = styled.select`
  ${fresponsive(css`
    position: relative;
    ${textStyles.titleS};
    color: ${colors.greenGray01};
    background: ${colors.greenGray02};
    width: 100%;
    height: 48px;
    padding: 0 16px;
    justify-content: space-between;
    align-items: center;
    border-radius: 10px;
    background: ${colors.greenGray02};
    transition: 0.2s ease-in-out;
    outline: none;

    &:hover {
      color: ${colors.white};
      border: 1px solid ${colors.greenSaturated02};
    }

    &:focus {
      outline-width: 1px;
      outline-style: solid;
      outline-color: #b3ed97;
    }

    &:invalid {
      color: #4b5845;
    }

    &:valid {
      color: ${colors.white};
    }

    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    background-image: none !important;
  `)}
`;

const SelectWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;
